// src/app/pages/app/ChatPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  MessageSquare, 
  WifiOff, 
  ShieldCheck, 
  Sparkles,
  Crown,
  Users,
  Clock,
  Send,
  CheckCircle2,
  XCircle
} from "lucide-react";

import {
  getMyThread,
  listMessages,
  markRead,
  sendMessage,
  presignUpload,
  uploadToPresignedUrl,
} from "../../features/chat/chatApi.js";
import { useChatSocket } from "../../features/chat/useChatSocket.js";
import MessageBubble from "../../components/chat/MessageBubble.jsx";
import ChatComposer from "../../components/chat/ChatComposer.jsx";

function nowLabel() {
  return new Intl.DateTimeFormat("en", { hour: "2-digit", minute: "2-digit" }).format(new Date());
}

function uuidLike() {
  return `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const OUTBOX_KEY = "ctx.chat.outbox.v1";

function loadOutbox() {
  try {
    return JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveOutbox(items) {
  localStorage.setItem(OUTBOX_KEY, JSON.stringify(items.slice(-200)));
}

export default function ChatPage() {
  const qc = useQueryClient();
  const bottomRef = useRef(null);

  const [outbox, setOutbox] = useState(() => loadOutbox());
  const [seen, setSeen] = useState(() => new Set());

  // 1) Get trader thread
  const threadQ = useQuery({
    queryKey: ["chatThread"],
    queryFn: getMyThread,
    staleTime: 60_000,
  });

  const threadId = threadQ.data?.threadId || null;

  // 2) Load messages
  const messagesQ = useQuery({
    queryKey: ["chatMessages", threadId],
    enabled: !!threadId,
    queryFn: () => listMessages({ threadId, limit: 50 }),
    refetchInterval: false,
  });

  const baseMessages = messagesQ.data?.items || [];

  // Merge base + outbox (optimistic)
  const merged = useMemo(() => {
    const all = [...baseMessages];

    const serverClientIds = new Set(all.map((m) => m.clientMessageId).filter(Boolean));
    for (const m of outbox) {
      if (m.clientMessageId && serverClientIds.has(m.clientMessageId)) continue;
      all.push(m);
    }

    all.sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime() || 0;
      const tb = new Date(b.createdAt || 0).getTime() || 0;
      return ta - tb;
    });

    return all;
  }, [baseMessages, outbox]);

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    scrollToBottom();
  }, [merged.length]);

  // 3) WS handler
  const onWsMessage = useCallback(
    (data) => {
      if (!data) return;

      if (data.type === "message" && data.message) {
        const msg = data.message;
        const key = msg.id || msg.clientMessageId;
        if (key && seen.has(key)) return;
        if (key) setSeen((s) => new Set([...s, key]));

        qc.setQueryData(["chatMessages", threadId], (old) => {
          const prev = old?.items || [];
          const exists = prev.some(
            (m) =>
              (m.id && m.id === msg.id) ||
              (m.clientMessageId && m.clientMessageId === msg.clientMessageId)
          );
          if (exists) return old;
          return { ...(old || {}), items: [...prev, msg] };
        });

        if (msg.id) {
          markRead({ threadId, lastReadMessageId: msg.id }).catch(() => {});
        }

        if (msg.clientMessageId) {
          setOutbox((prev) => {
            const next = prev.filter((x) => x.clientMessageId !== msg.clientMessageId);
            saveOutbox(next);
            return next;
          });
        }
      }

      if (data.type === "delivery" && data.clientMessageId) {
        setOutbox((prev) => {
          const next = prev.map((m) =>
            m.clientMessageId === data.clientMessageId ? { ...m, status: data.status } : m
          );
          saveOutbox(next);
          return next;
        });
      }
    },
    [qc, threadId, seen]
  );

  const ws = useChatSocket({ threadId, onMessage: onWsMessage });

  // 4) Fallback polling when WS disconnected
  useEffect(() => {
    if (!threadId) return;
    if (ws.status === "connected") return;

    const t = window.setInterval(() => {
      qc.invalidateQueries({ queryKey: ["chatMessages", threadId] });
    }, 4000);

    return () => window.clearInterval(t);
  }, [ws.status, threadId, qc]);

  // 5) Persist outbox
  useEffect(() => saveOutbox(outbox), [outbox]);

  const pushOutbox = (draft) => {
    setOutbox((prev) => {
      const next = [...prev, draft].slice(-200);
      saveOutbox(next);
      return next;
    });
  };

  const trySend = async (payload) => {
    const wsOk = ws.sendWs({ type: "send", payload });
    if (wsOk) return;
    await sendMessage(payload);
  };

  const sendText = async (text) => {
    if (!threadId) return;
    const clientMessageId = uuidLike();

    const draft = {
      threadId,
      clientMessageId,
      kind: "text",
      text,
      sender: "ME",
      status: "pending",
      createdAtLabel: nowLabel(),
      createdAt: new Date().toISOString(),
    };

    pushOutbox(draft);

    try {
      await trySend({ threadId, clientMessageId, kind: "text", text });
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "sent" } : m))
      );
    } catch {
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const sendGifUrl = async (gifUrl) => {
    if (!threadId) return;
    const clientMessageId = uuidLike();

    const draft = {
      threadId,
      clientMessageId,
      kind: "gif",
      gifUrl,
      sender: "ME",
      status: "pending",
      createdAtLabel: nowLabel(),
      createdAt: new Date().toISOString(),
    };

    pushOutbox(draft);

    try {
      await trySend({ threadId, clientMessageId, kind: "gif", gifUrl });
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "sent" } : m))
      );
    } catch {
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const sendFile = async (file) => {
    if (!threadId) return;
    const clientMessageId = uuidLike();

    const draft = {
      threadId,
      clientMessageId,
      kind: "file",
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      sender: "ME",
      status: "pending",
      createdAtLabel: nowLabel(),
      createdAt: new Date().toISOString(),
    };

    pushOutbox(draft);

    try {
      const pres = await presignUpload({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
      });

      await uploadToPresignedUrl(pres.uploadUrl, file, file.type);

      await trySend({
        threadId,
        clientMessageId,
        kind: "file",
        s3Key: pres.s3Key,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
      });

      setOutbox((prev) =>
        prev.map((m) =>
          m.clientMessageId === clientMessageId
            ? {
                ...m,
                status: "sent",
                s3Key: pres.s3Key,
                fileName: file.name,
                mimeType: file.type || "application/octet-stream",
                size: file.size,
              }
            : m
        )
      );
    } catch {
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const sendVoiceBlob = async (blob) => {
    if (!threadId) return;
    const clientMessageId = uuidLike();
    const file = new File([blob], `voice_${Date.now()}.webm`, { type: "audio/webm" });

    const draft = {
      threadId,
      clientMessageId,
      kind: "voice",
      fileName: file.name,
      mimeType: file.type || "audio/webm",
      size: file.size,
      sender: "ME",
      status: "pending",
      createdAtLabel: nowLabel(),
      createdAt: new Date().toISOString(),
    };

    pushOutbox(draft);

    try {
      const pres = await presignUpload({
        fileName: file.name,
        mimeType: file.type || "audio/webm",
        size: file.size,
      });

      await uploadToPresignedUrl(pres.uploadUrl, file, file.type);

      await trySend({
        threadId,
        clientMessageId,
        kind: "voice",
        s3Key: pres.s3Key,
        fileName: file.name,
        mimeType: file.type || "audio/webm",
        size: file.size,
      });

      setOutbox((prev) =>
        prev.map((m) =>
          m.clientMessageId === clientMessageId
            ? {
                ...m,
                status: "sent",
                s3Key: pres.s3Key,
                fileName: file.name,
                mimeType: file.type || "audio/webm",
                size: file.size,
              }
            : m
        )
      );
    } catch {
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const retry = async (msg) => {
    if (!threadId) return;
    if (!msg?.clientMessageId) return;

    setOutbox((prev) =>
      prev.map((m) => (m.clientMessageId === msg.clientMessageId ? { ...m, status: "pending" } : m))
    );

    try {
      const payload = {
        threadId,
        clientMessageId: msg.clientMessageId,
        kind: msg.kind,
        text: msg.text,
        gifUrl: msg.gifUrl,
        s3Key: msg.s3Key,
        fileName: msg.fileName,
        mimeType: msg.mimeType,
        size: msg.size,
      };

      await trySend(payload);

      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === msg.clientMessageId ? { ...m, status: "sent" } : m))
      );
    } catch {
      setOutbox((prev) =>
        prev.map((m) => (m.clientMessageId === msg.clientMessageId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const loading = threadQ.isLoading || (threadId && messagesQ.isLoading);
  const disabled = loading || !threadId;

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col p-6 bg-gradient-to-br from-[#0a0a1a] via-[#0f0f2a] to-[#1a0a2e]">
      {/* Decorative background */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-indigo-600/5 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-purple-600/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative flex flex-col h-full">
        {/* Header - Enhanced */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/25">
              <MessageSquare size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                Support Chat
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/20">
                  Secure
                </span>
              </h1>
              <p className="text-sm text-white/40">Trader ↔ Moderator • Real-time messaging</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60">
              <ShieldCheck size={14} className="text-emerald-400/60" />
              <span className="text-xs">Encrypted</span>
            </div>

            {ws.status !== "connected" ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300/80">
                <WifiOff size={14} />
                <span className="text-xs">Reconnecting…</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs">Connected</span>
              </div>
            )}
          </div>
        </div>

        {/* Messages Container - Enhanced Glass Effect */}
        <div className="flex-1 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden shadow-2xl shadow-black/20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative h-full flex flex-col">
            {/* Messages Header */}
            <div className="px-5 py-3 border-b border-white/5 bg-white/5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                  <Users size={14} className="text-indigo-400" />
                </div>
                <span className="text-sm text-white/60">Live Conversation</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/20">
                <Clock size={12} />
                <span>Real-time</span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-3 text-white/40">
                    <div className="h-5 w-5 rounded-full border-2 border-indigo-400/30 border-t-indigo-400 animate-spin" />
                    <span className="text-sm">Loading chat…</span>
                  </div>
                </div>
              )}

              {!loading && merged.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="text-5xl mb-4 opacity-30">💬</div>
                  <div className="text-white/40 font-medium">No messages yet</div>
                  <div className="text-sm text-white/20 mt-1">Send a message to your moderator to get started</div>
                </div>
              )}

              {merged.map((m) => (
                <MessageBubble key={m.id || m.clientMessageId} msg={m} onRetry={retry} />
              ))}

              <div ref={bottomRef} />
            </div>

            {/* Composer Area */}
            <div className="border-t border-white/5 bg-white/5 p-3 flex-shrink-0">
              <ChatComposer
                disabled={disabled}
                onSendText={sendText}
                onSendGifUrl={sendGifUrl}
                onSendFile={sendFile}
                onSendVoiceBlob={sendVoiceBlob}
              />
            </div>
          </div>
        </div>

        {/* Footer Status */}
        <div className="mt-3 text-center">
          <span className="flex items-center justify-center gap-2 text-xs text-white/20">
            <Sparkles size={12} className="text-indigo-400/50" />
            Messages are end-to-end encrypted and securely stored
          </span>
        </div>
      </div>
    </div>
  );
}
